/**
 * PhotoUpload Component
 *
 * Photo upload for listing sections with featured image selection.
 * Displays images in a grid with star icons to mark featured image.
 */

import { useState, useCallback, useMemo, type DragEvent } from "react";
import { Star, X, Upload } from "lucide-react";
import { useTranslation } from "@/shared/hooks";
import type { Image as ListingImage } from "@/shared/types/primitives";

interface PhotoUploadProps {
  sectionId: string;
  sectionLabel: string;
  images?: ListingImage[];
  onPhotosChange?: (files: File[]) => void;
  onPhotoDelete?: (imageUrl: string) => void;
  onPhotoReorder?: (reorderedImages: ListingImage[]) => void;
  onToggleFeatured?: (imageUrl: string) => void;
  allImagesBySection?: Record<string, ListingImage[]>;
}

export function PhotoUpload({
  sectionId: _sectionId,
  sectionLabel,
  images = [],
  onPhotosChange,
  onPhotoDelete,
  onPhotoReorder,
  onToggleFeatured,
  allImagesBySection = {},
}: PhotoUploadProps) {
  const t = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Check if any image across all sections is already featured
  const hasFeaturedImage = useMemo(() => {
    return Object.values(allImagesBySection).some((sectionImages) =>
      sectionImages.some((img) => img.is_featured),
    );
  }, [allImagesBySection]);

  const handleFileInput = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0 || !onPhotosChange) return;

      setUploading(true);
      try {
        await onPhotosChange(Array.from(files));
      } finally {
        setUploading(false);
      }
    },
    [onPhotosChange],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragActive(false);
      handleFileInput(e.dataTransfer.files);
    },
    [handleFileInput],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  }, []);

  /**
   * Handle thumbnail drag start (for reordering)
   */
  const handleThumbnailDragStart = useCallback(
    (e: DragEvent<HTMLDivElement>, index: number) => {
      if (!onPhotoReorder) return;
      e.stopPropagation();
      e.dataTransfer.effectAllowed = "move";
      setDraggedIndex(index);
    },
    [onPhotoReorder],
  );

  /**
   * Handle thumbnail drag over (for reordering)
   */
  const handleThumbnailDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>, index: number) => {
      e.preventDefault();
      e.stopPropagation();
      if (!onPhotoReorder || draggedIndex === null) return;
      e.dataTransfer.dropEffect = "move";
      setDragOverIndex(index);
    },
    [onPhotoReorder, draggedIndex],
  );

  /**
   * Handle thumbnail drop (for reordering)
   */
  const handleThumbnailDrop = useCallback(
    (e: DragEvent<HTMLDivElement>, dropIndex: number) => {
      e.preventDefault();
      e.stopPropagation();

      if (
        !onPhotoReorder ||
        draggedIndex === null ||
        draggedIndex === dropIndex
      ) {
        setDraggedIndex(null);
        setDragOverIndex(null);
        return;
      }

      const newImages = [...images];
      const [draggedImage] = newImages.splice(draggedIndex, 1);

      if (!draggedImage) {
        setDraggedIndex(null);
        setDragOverIndex(null);
        return;
      }

      newImages.splice(dropIndex, 0, draggedImage);

      onPhotoReorder(newImages);
      setDraggedIndex(null);
      setDragOverIndex(null);
    },
    [onPhotoReorder, draggedIndex, images],
  );

  /**
   * Handle thumbnail drag end (for reordering)
   */
  const handleThumbnailDragEnd = useCallback(() => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700">
          {sectionLabel}
        </label>
        {onToggleFeatured && (
          <span className="text-xs text-gray-500">
            {t("createListingFeaturedPhotoHint")}
          </span>
        )}
      </div>

      {/* Upload Area */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragActive
            ? "border-blue-500 bg-blue-50"
            : "border-gray-300 hover:border-gray-400"
        }`}
      >
        <input
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/webp"
          multiple
          onChange={(e) => handleFileInput(e.target.files)}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={uploading}
        />
        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
        <p className="text-sm text-gray-600">
          {uploading ? t("fileUploadUploading") : t("photoUploadHint")}
        </p>
        <p className="text-xs text-gray-500 mt-1">{t("photoUploadMax")}</p>
      </div>

      {/* Uploaded Images Grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {images.map((img, idx) => (
            <div
              key={img.url}
              className={`relative group transition-all ${
                draggedIndex === idx
                  ? "opacity-50 scale-95"
                  : dragOverIndex === idx
                    ? "scale-105 ring-2 ring-red-500"
                    : ""
              } ${onPhotoReorder ? "cursor-move" : ""}`}
              draggable={!!onPhotoReorder}
              onDragStart={(e) => handleThumbnailDragStart(e, idx)}
              onDragOver={(e) => handleThumbnailDragOver(e, idx)}
              onDrop={(e) => handleThumbnailDrop(e, idx)}
              onDragEnd={handleThumbnailDragEnd}
            >
              <img
                src={img.thumbnail_url || img.url}
                alt={`Photo ${idx + 1}`}
                className="w-full h-32 object-cover rounded-lg pointer-events-none"
              />

              {/* Action Buttons Overlay */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all rounded-lg flex items-center justify-center gap-2">
                {/* Featured Toggle Button */}
                {onToggleFeatured && (
                  <button
                    type="button"
                    onClick={() => onToggleFeatured(img.url)}
                    disabled={hasFeaturedImage && !img.is_featured}
                    className={`opacity-0 group-hover:opacity-100 transition-opacity p-2 rounded-full ${
                      img.is_featured
                        ? "bg-yellow-500 text-white"
                        : hasFeaturedImage
                          ? "bg-gray-400 text-white cursor-not-allowed"
                          : "bg-white text-gray-700 hover:bg-yellow-100"
                    }`}
                    title={
                      img.is_featured
                        ? t("createListingRemoveFeatured")
                        : hasFeaturedImage
                          ? t("createListingFeaturedPhoto") +
                            " (already selected)"
                          : t("createListingSetFeatured")
                    }
                  >
                    <Star
                      className="w-4 h-4"
                      fill={img.is_featured ? "currentColor" : "none"}
                    />
                  </button>
                )}

                {/* Delete Button */}
                {onPhotoDelete && (
                  <button
                    type="button"
                    onClick={() => onPhotoDelete(img.url)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity bg-red-600 text-white p-2 rounded-full hover:bg-red-700"
                    title="Delete"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Featured Badge (always visible) */}
              {img.is_featured && (
                <div className="absolute top-2 left-2 bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                  <Star className="w-3 h-3" fill="currentColor" />
                  Featured
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
